import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Loader, SdirCheckbox } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useCurrentApplication } from '../../../services/hooks';
import { ApplicationStatus } from '../../../types';
import { confirmationApi } from '../../../httpclient';
import { buildRequirementKey } from '../../../services/certreq/requirementKeys';
import { nodeText, UserRequirementNode } from '../../../services/certreq/userView/userRequirements';
import { UserViewTexts } from '../../../services/certreq/userView/texts';

interface RequirementDeclarationProps {
  node: UserRequirementNode;
  certificateCode: string;
  texts: UserViewTexts;
}

/**
 * Erklæringen søkeren krysser av for — kravtypen som IKKE dokumenteres med en
 * fil.
 *
 * Avkryssingen lagres som en `ConfirmedQualification` på søknaden gjennom det
 * eksisterende bekreftelses-endepunktet, akkurat som den gamle
 * kvalifikasjonssiden gjorde det. Nøkkelen er den samme som vedleggene bruker,
 * `certreq:{kode}#{requirementId}`, og teksten som lagres (`confirmedText`) er
 * **den søkeren faktisk leste** — radteksten på hennes eget språk. Det er den
 * teksten som havner i det arkiverte søknadsdokumentet, så den kan ikke være en
 * annen enn den som stod på skjermen.
 *
 * URI-en sendes prosentkodet, som i legacy: den generte klienten koder
 * ruteparameteren en gang til, og backend pakker begge lagene ut igjen
 * (`UnescapeAndLowerCase`).
 *
 * `confirmedText` har en grense på 1000 tegn i backend
 * (`QualificationConfirmationApiModel`). De fire SID-erklæringene ligger på
 * 89–671 tegn, så det er god margin — men teksten forkortes bevisst IKKE her:
 * en juridisk erklæring som er klippet i to er verre enn en feilmelding.
 *
 * **Kun i kladd.** `QualificationConfirmationController` krever at søknaden står
 * i Draft, og avviser alt annet. Da skal boksen være lesbar, men låst — søkeren
 * skal se hva hun har erklært, ikke møte en knapp som gir feilmelding.
 */
export const RequirementDeclaration: React.FC<RequirementDeclarationProps> = ({
  node,
  certificateCode,
  texts,
}) => {
  const { application, refresh } = useCurrentApplication();
  const [confirmed, setConfirmed] = useState(node.confirmed);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
   * Søknaden er fasit. Etter en vellykket lagring kommer den friske søknaden
   * inn her, og etter en feilet lagring ruller den den lokale haken tilbake.
   */
  useEffect(() => setConfirmed(node.confirmed), [node.confirmed]);

  const applicationUid = application.result?.uid || '';
  const isDraft = application.result?.applicationStatus === ApplicationStatus.Draft;
  const requirementKey = buildRequirementKey(certificateCode, node.requirementId);
  const text = nodeText(node);

  const handleChange = useCallback(
    async (next: boolean) => {
      /*
       * `disabled` på SdirCheckbox er bare CSS (`pointer-events: none`) — selve
       * klikkflaten ligger på wrapperen og kaller alltid tilbake. Sperren må
       * derfor stå her også, ellers ville en innsendt søknad forsøkt en lagring
       * backend uansett avviser (`MustBeInState(Draft)`).
       */
      if (!isDraft || !applicationUid || !requirementKey || saving) return;
      // Legacy koder URI-en før den generte klienten koder den en gang til.
      const escapedUri = encodeURIComponent(requirementKey);

      setConfirmed(next);
      setSaving(true);
      setFailed(false);
      try {
        if (next) {
          await confirmationApi.v1QualificationconfirmationConfirmqualificationBodyPut(
            applicationUid,
            escapedUri,
            { confirmedText: text }
          );
        } else {
          await confirmationApi.v1QualificationconfirmationRemovequalificationconfirmationUriDelete(
            applicationUid,
            escapedUri
          );
        }
        /*
         * STILLE oppfriskning. Den vanlige nuller søknaden og setter `loading`,
         * og da avmonterer `EditApplicationGuard` hele kravsiden til fordel for
         * en naken spinner: ekspanderte grupper og «Vis andre alternativer»
         * nullstilles, og på sjøfartskortet må søkeren navigere seg fram igjen
         * mellom de to erklæringene i samme alternativvei. Haken hun nettopp
         * satte står allerede riktig lokalt; det eneste vi venter på er at
         * søknaden bekrefter den.
         */
        refresh?.(true);
      } catch {
        // Haken rulles tilbake: den skal aldri vise noe annet enn det søknaden
        // faktisk har lagret.
        setConfirmed(!next);
        setFailed(true);
      } finally {
        setSaving(false);
      }
    },
    [applicationUid, isDraft, refresh, requirementKey, saving, text]
  );

  // Uten requirementId finnes ingen stabil nøkkel, og erklæringen kan ikke
  // knyttes til noe. Da sier vi fra i stedet for å tilby en boks som ikke virker.
  if (!requirementKey) {
    return <DeclarationNote>{texts.declarationMissingKey}</DeclarationNote>;
  }

  return (
    <Declaration data-testid={`requirement-declaration-${node.requirementId}`}>
      <SdirCheckbox
        checked={confirmed}
        disabled={!isDraft || saving}
        title={texts.declarationConfirm}
        aria-label={texts.declarationConfirmFor(text)}
        onClick={() => handleChange(!confirmed)}
      />
      {saving && <Loader />}
      {!isDraft && <DeclarationNote>{texts.declarationLocked}</DeclarationNote>}
      {failed && <ErrorMessage error={texts.declarationError} />}
    </Declaration>
  );
};

/**
 * Avkryssingen står på egen linje under radteksten, som opplastingsknappen gjør
 * på de andre kravene — handlingen kravet ber om skal ikke gjemme seg ytterst
 * til høyre i en tett liste.
 */
const Declaration = styled.div`
  margin-top: 0.5rem;

  label {
    color: var(--status-text);
  }
`;

const DeclarationNote = styled.p`
  margin: 0.4rem 0 0;
  font-size: 14px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.font.text75};
`;

export default RequirementDeclaration;
